import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { CSVLink } from 'react-csv';
import PageHeader from './PageHeader';
import { AxiosGet, AxiosPost } from '../http/Axios';
import { FaEnvelope } from 'react-icons/fa';
import './RegistrationReport.css';

const modalStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    background: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '300px',
    textAlign: 'center'
  }
};

const RegistrationReport = () => {
  const [records, setRecords] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [familyName, setFamilyName] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  const [showModal, setShowModal] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalStep, setModalStep] = useState('confirm');
  const [errorMessage, setErrorMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'registeredDate', direction: 'descending' });

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const renderHeader = (label, key) => (
    <th onClick={() => handleSort(key)} style={{ cursor: 'pointer' }}>
      {label}
      {sortConfig.key === key && (
        <span>{sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}</span>
      )}
    </th>
  );

  const openResendModal = (email) => {
    setModalEmail(email);
    setModalStep('confirm');
    setErrorMessage('');
    setShowModal(true);
  };

  const handleResendYes = async () => {
    try {
      const url = `${process.env.REACT_APP_ADMIN_SERVICE}/registration/send-on-demand-registration-email?email=${encodeURIComponent(modalEmail)}`;
      await AxiosPost(url, {});
      setModalStep('success');
    } catch (err) {
      console.error(`Error resending registration for ${modalEmail}`, err);
      setErrorMessage(err?.response?.data || 'Unknown error');
      setModalStep('fail');
    }
  };

  // Function to format date as mm/dd/yyyy hh24:mi:ss
  const formatDate = (dateString, allowNull = false) => {
    if (!dateString) return allowNull ? null : 'N/A';
    const date = new Date(dateString);

    if (isNaN(date)) return allowNull ? null : 'N/A';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    document.title = 'LeapFrog Lab Registration Admin Portal';

    const navEntries = performance.getEntriesByType('navigation');
    const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';

    if (isReload) {
      const saved = sessionStorage.getItem('registrationReport');
      if (saved) {
        try {
          const { familyName: savedFamily, results } = JSON.parse(saved);
          if (savedFamily) setFamilyName(savedFamily);
          if (results?.length) setRecords(results);
        } catch (err) {
          console.error('Failed to restore registrationReport from sessionStorage:', err);
        }
      }
    }
  }, []);

  const handleFamilyNameChange = (e) => {
    setFamilyName(e.target.value);
  };

  const handleGetFamilies = async () => {
    const params = new URLSearchParams();
    if (familyName) {
      params.append('familyName', familyName.trim().toLowerCase());
    }
    const url =
      process.env.REACT_APP_ADMIN_SERVICE +
      `/registration-report?${params.toString()}`;
    try {
      const response = await AxiosGet(url);
      setRecords(response);

      if (!response || response.length === 0) {
        setNoResults(true);
      } else {
        setNoResults(false);
      }

      sessionStorage.setItem('registrationReport', JSON.stringify({
        familyName,
        results: response
      }));
    } catch (err) {
      console.log(err);
      setRecords([]);
      setNoResults(true);
    }
  };

  const handleResetParameters = () => {
    setFamilyName('');
    setRecords([]);
    setNoResults(false);
    sessionStorage.removeItem('registrationReport');
  };

  const exportToExcel = () => {
    const formattedRecords = records.map((record) => ({
      'Family Id': record.id || 'N/A',
      'Email Address': record.email || 'N/A',
      'Family Name': record.familyName || 'N/A',
      Guardians: record.firstName || 'N/A',
      'Date Registered': formatDate(record.registeredDate),
      'Confirmation Email Sent': record.firstEmailSentDate ? 'Yes' : 'No',
      'User Confirmed': record.registeredDate ? 'Yes' : 'No',
      'Second Notice Sent Date': formatDate(record.secondEmailSentDate),
      'Okay To Contact': record.okayToContactIndicator === 'Y' ? 'Yes' : 'No',
    }));

    const ws = XLSX.utils.json_to_sheet(formattedRecords);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Registration Report');
    XLSX.writeFile(wb, 'registration_report.xlsx');
  };

  const sortedRecords = [...records].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'registeredDate' || sortConfig.key === 'firstEmailSentDate' || sortConfig.key === 'secondEmailSentDate') {
      aValue = aValue ? new Date(aValue) : 0;
      bValue = bValue ? new Date(bValue) : 0;
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  return (
    <div className="dashboard registration-report">
      <PageHeader title="Registration Report" />
      <main className="page-content">
        <form className="search-form">

          <div className="form-group">
            <label htmlFor="family-name-input">Family Name:</label>
            <input
              id="family-name-input"
              type="text"
              value={familyName}
              onChange={handleFamilyNameChange}
              className="form-control"
            />
          </div>
          <div className="button-group">
            <button type="button" className="btn btn-primary" onClick={handleGetFamilies}>
              Search Families
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleResetParameters}>
              Reset Parameters
            </button>
          </div>

          {noResults && (
            <div className="no-results-message">
              No results found
            </div>
          )}

        </form>

        {!noResults && records.length > 0 && (
          <div className="results-container">
            <table className="results-table">
              <thead>
              <tr>
                {renderHeader('Family Id', 'id')}
                {renderHeader('Email Address', 'email')}
                {renderHeader('Family Name', 'familyName')}
                {renderHeader('Guardians', 'firstName')}
                {renderHeader('Date Registered', 'registeredDate')}
                {renderHeader('Confirmation Email Sent', 'firstEmailSentDate')}
                {renderHeader('User Confirmed', 'registeredDate')}
                {renderHeader('Second Notice Sent Date', 'secondEmailSentDate')}
                {renderHeader('Okay To Contact', 'okayToContactIndicator')}
                <th>Resend Reg Email</th>
              </tr>
              </thead>
              <tbody>
              {sortedRecords.map((record, index) => (
                <tr key={index}>
                  <td>{record.id || 'N/A'}</td>
                  <td>{record.email || 'N/A'}</td>
                  <td>{record.familyName || 'N/A'}</td>
                  <td>{record.firstName || 'N/A'}</td>
                  <td>{formatDate(record.registeredDate, true)}</td>
                  <td>{record.firstEmailSentDate ? 'Yes' : 'No'}</td>
                  <td>{record.registeredDate ? 'Yes' : 'No'}</td>
                  <td>{formatDate(record.secondEmailSentDate, true)}</td>
                  <td>{record.okayToContactIndicator === 'Y' ? 'Yes' : 'No'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {!record.registeredDate && (
                      <FaEnvelope
                        style={{ cursor: 'pointer', color: '#007bff' }}
                        onClick={() => openResendModal(record.email)}
                      />
                    )}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>

            <div className="export-options">
              <button className="btn btn-primary" onClick={exportToExcel}>
                Export to Excel
              </button>
              <CSVLink
                data={records.map((record) => ({
                  'Family Id': record.id || 'N/A',
                  'Email Address': record.email || 'N/A',
                  'Family Name': record.familyName || 'N/A',
                  Guardians: record.firstName || 'N/A',
                  'Date Registered': formatDate(record.registeredDate, true),
                  'Confirmation Email Sent': record.firstEmailSentDate ? 'Yes' : 'No',
                  'User Confirmed': record.registeredDate ? 'Yes' : 'No',
                  'Second Notice Sent Date': formatDate(record.secondEmailSentDate, true),
                  'Okay To Contact': record.okayToContactIndicator === 'Y' ? 'Yes' : 'No',
                }))}
                filename="families.csv"
                className="btn btn-secondary"
              >
                Export to CSV
              </CSVLink>
            </div>
          </div>
        )}

        {showModal && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
              {modalStep === 'confirm' && (
                <>
                  <h3>Resend email?</h3>
                  <p>Do you want to resend the verification email to: {modalEmail}</p>
                  <div className="button-group">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleResendYes}
                    >
                      Yes
                    </button>
                  </div>
                </>
              )}

              {modalStep === 'success' && (
                <>
                  <h3>Email sent</h3>
                  <p>The email has been resent to: {modalEmail}</p>
                  <div className="button-group">
                    <button className="btn btn-primary" onClick={() => setShowModal(false)}>OK</button>
                  </div>
                </>
              )}

              {modalStep === 'fail' && (
                <>
                  <h3>Failed to send</h3>
                  <p>There was a problem resending the email to: {modalEmail}</p>
                  {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                  <div className="button-group">
                    <button className="btn btn-primary" onClick={() => setShowModal(false)}>OK</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RegistrationReport;
